// Catalog.ID Client — Operator Voucher Backoffice
// ============================================================================
// Mint / list / revoke registration vouchers (registration-restructure §7.1,
// M7). This is the Stripe-less sales counter: offline and bank-invoice sales,
// comps, and support replacements for a code someone lost.
//
// Lives in the operator workspace, so it inherits that workspace's visibility
// gate (`shell.setWorkspaceHidden('operator', !session.isOperator())` in
// app.ts) — and the server gates every call on derived operator authority
// regardless, since a hidden pane is a convenience, not a permission.
//
// Two things this deliberately does NOT do:
//  - show codes for anything but a fresh mint. The server stores only hashes,
//    so past codes genuinely do not exist to show.
//  - name the payment provider. Registration-restructure §14.4 reverses decision
//    G4: a purchased row carries an opaque BATCH REFERENCE (the payment Worker's
//    HMAC of the checkout session, under a secret this node does not hold), not a
//    Stripe session id. It still groups a batch and identifies what a refund
//    touched, which is what support actually needs; joining it to a payer takes
//    the Worker's secret and Stripe as well as this database.

import { createElement, createFieldGroup } from '@catalog-shell';
import type { ConfirmOptions, PaneType, PaneTypeRenderContext } from '@catalog-shell';
import { formatApiError } from '../api/client';
import { listVouchers, mintVouchers, revokeVoucher } from '../api/voucher';
import { fetchRegistrationConfig, type GiftOffer } from '../auth/payment-flow';
import type { VoucherSummary, VoucherStatus, RegistrationType } from '../api/voucher';
import { renderVoucherCodes } from '../auth/voucher-code-display';
import { PANE_IDS } from '../shell-config';

const PAGE_SIZE = 50;

/** Whole BIT with thousands separators: `10,000 BIT`. */
const bitText = (bit: number): string => `${new Intl.NumberFormat('en-US').format(bit)} BIT`;

/**
 * The gift toggle's label for a code of `type` (apps/bitcash/docs/plans/bitcash-v2.md M12, D70), or
 * null when this node gifts no code of that type — then no toggle is offered.
 * An organization code carries two gifts: its own lock and the founder's.
 */
export function giftOfferLabel(type: RegistrationType, gifts: GiftOffer | null): string | null {
    if (gifts === null) return null;
    const own = gifts[type];
    if (own === null) return null;
    if (type !== 'organization') return `Fund each code with a gift of ${bitText(own)}`;
    if (gifts.organizationFounder === null) return null;
    return `Fund each code with a gift of ${bitText(own)} (+ ${bitText(gifts.organizationFounder)} for the founder)`;
}

/** What the operator reads when the ledger stopped a gift-funded mint part-way; null when all were funded.
 *  The server's sentence already says how many codes were funded and minted. */
export function giftRefusalNote(resp: { refused?: { code: string; error: string } | null; requested?: number; codes: string[] }): string | null {
    if (!resp.refused) return null;
    return resp.refused.error;
}

/**
 * Only unredeemed rows can be revoked — the server refuses the rest, because a
 * reserved or consumed voucher already has a registration riding on it, and a
 * revoked or refunded one is already dead.
 */
export function canRevokeVoucher(status: VoucherStatus): boolean {
    return status === 'unredeemed';
}

/**
 * Revoking kills a code somebody may be holding, and it cannot be minted back:
 * the server stores only hashes. Explicit answer required, like every other
 * destructive action in this client.
 */
export function voucherRevokeConfirm(voucherId: number): ConfirmOptions {
    return {
        title: 'Revoke voucher?',
        message: `Revoke voucher #${voucherId}? Whoever holds the code will not be able to register `
            + 'with it, and it cannot be restored — the server keeps only its hash.',
        confirmLabel: 'Revoke',
        cancelLabel: 'Cancel',
        confirmVariant: 'no',
        requireExplicitChoice: true,
    };
}

const TYPE_LABELS: Record<string, string> = {
    personal: 'Personal',
    organization: 'Org',
    delegate: 'Delegate',
};

export function createVouchersPane(): PaneType {
    return {
        typeId: PANE_IDS.vouchers,
        kind: 'leaf',
        defaultTitle: 'Vouchers',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderVouchers(container, ctx);
            return container;
        },
    };
}

async function renderVouchers(container: HTMLElement, ctx: PaneTypeRenderContext): Promise<void> {
    const errorEl = createElement('div', { className: 'cid-pane-error' });
    container.appendChild(errorEl);

    const mintSection = createFieldGroup({ title: 'Mint vouchers' });
    container.appendChild(mintSection.element);

    const listSection = createElement('div');
    container.appendChild(listSection);

    // ── Mint form ───────────────────────────────────────────────────────────

    const mintBody = createElement('div', { className: 'cid-wizard-step' });
    mintBody.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Codes are shown once, here, and never again — the server keeps only their hashes. '
            + 'Copy them before leaving this pane.',
    }));

    const typeSelect = createElement('select', { className: 'cid-auth-input' });
    for (const [value, label] of [
        ['personal', 'Personal identity'],
        ['organization', 'Organization'],
        // Delegate slots (registration-restructure §13.4). An operator mint is the
        // support/comp path and stays UNBOUND, so the code reaches any organization.
        ['delegate', 'Delegate slot'],
    ] as const) {
        const opt = createElement('option', { textContent: label });
        opt.value = value;
        typeSelect.appendChild(opt);
    }

    const qtyInput = createElement('input', { className: 'cid-auth-input' });
    qtyInput.type = 'number';
    qtyInput.min = '1';
    qtyInput.max = '100';
    qtyInput.value = '1';

    const noteInput = createElement('input', { className: 'cid-auth-input' });
    noteInput.type = 'text';
    noteInput.placeholder = 'Why (invoice number, support ticket, …)';

    for (const [label, field] of [
        ['Type', typeSelect],
        ['How many', qtyInput],
        ['Note', noteInput],
    ] as const) {
        const row = createElement('div', { className: 'cid-auth-field' });
        row.appendChild(createElement('label', { className: 'cid-wizard-text', textContent: label }));
        row.appendChild(field);
        mintBody.appendChild(row);
    }

    // Track N (apps/bitcash/docs/plans/bitcash-v2.md M12): the owner's codes carry an Administrator
    // gift, made by this node's online grant key before each row is written.
    // Offered only for a type the node gifts.
    const giftRow = createElement('label', { className: 'cid-wizard-text' });
    const giftBox = createElement('input');
    giftBox.type = 'checkbox';
    giftBox.checked = true;
    const giftText = createElement('span');
    giftRow.appendChild(giftBox);
    giftRow.appendChild(giftText);
    giftRow.style.display = 'none';
    mintBody.appendChild(giftRow);
    let gifts: GiftOffer | null = null;
    const syncGift = (): void => {
        const label = giftOfferLabel(typeSelect.value as RegistrationType, gifts);
        giftRow.style.display = label === null ? 'none' : '';
        giftText.textContent = label === null ? '' : ` ${label}`;
    };
    typeSelect.addEventListener('change', syncGift);
    void fetchRegistrationConfig().then(cfg => { gifts = cfg.gifts; syncGift(); });

    const mintBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Mint',
    });
    mintBody.appendChild(mintBtn);

    const mintedArea = createElement('div');
    mintBody.appendChild(mintedArea);
    mintSection.body.appendChild(mintBody);

    mintBtn.addEventListener('click', () => {
        const quantity = Number(qtyInput.value);
        if (!Number.isInteger(quantity) || quantity < 1 || quantity > 100) {
            showError('Choose a whole number of vouchers between 1 and 100.');
            return;
        }
        mintBtn.setAttribute('disabled', '');
        mintBtn.textContent = 'Minting…';
        errorEl.classList.remove('cid-pane-error--visible');

        void (async () => {
            try {
                const gift = giftRow.style.display !== 'none' && giftBox.checked;
                const batch = await mintVouchers({
                    quantity,
                    registrationType: typeSelect.value as RegistrationType,
                    note: noteInput.value.trim() || undefined,
                    ...(gift ? { gift: true } : {}),
                });
                showMinted(batch.codes);
                const refusal = giftRefusalNote(batch);
                if (refusal !== null) showError(refusal);
                noteInput.value = '';
                await refresh();
            } catch (e) {
                showError(formatApiError(e, 'Could not mint vouchers'));
            } finally {
                mintBtn.removeAttribute('disabled');
                mintBtn.textContent = 'Mint';
            }
        })();
    });

    function showMinted(codes: string[]): void {
        // The preamble above the form already carries the once-only warning, so
        // this instance shows the codes without repeating it.
        renderVoucherCodes(mintedArea, codes, {
            headingTag: 'h4',
            showNotice: false,
            heading: codes.length === 1 ? 'New code' : `${codes.length} new codes`,
        });
    }

    // ── Listing ─────────────────────────────────────────────────────────────

    let statusFilter: VoucherStatus | '' = '';
    let offset = 0;

    // The status filter is a filter over the list, not a field of a form: it
    // gets its own row directly above the table, in the shell's filter-control
    // dress (shell styles/panes.less "Filter Controls").
    const filterRow = createElement('div', { className: 'cs-filter-controls' });
    const filterControl = createElement('div', { className: 'cs-filter-control' });
    filterControl.appendChild(createElement('label', {
        className: 'cs-filter-label', textContent: 'Show',
    }));
    const filterSelect = createElement('select', { className: 'cs-filter-select' });
    for (const [value, label] of [
        ['', 'All'],
        ['unredeemed', 'Unredeemed'],
        ['reserved', 'Reserved'],
        ['consumed', 'Consumed'],
        ['revoked', 'Revoked'],
        ['refunded', 'Refunded'],
    ] as const) {
        const opt = createElement('option', { textContent: label });
        opt.value = value;
        filterSelect.appendChild(opt);
    }
    filterSelect.addEventListener('change', () => {
        statusFilter = filterSelect.value as VoucherStatus | '';
        offset = 0;
        void refresh();
    });
    filterControl.appendChild(filterSelect);
    filterRow.appendChild(filterControl);
    listSection.appendChild(filterRow);

    const tableWrap = createElement('div');
    tableWrap.style.overflowX = 'auto';
    listSection.appendChild(tableWrap);

    const pager = createElement('div', { className: 'cid-auth-link-row' });
    listSection.appendChild(pager);

    async function refresh(): Promise<void> {
        try {
            const page = await listVouchers({ status: statusFilter, limit: PAGE_SIZE, offset });
            renderTable(page.vouchers);
            renderPager(page.total);
        } catch (e) {
            showError(formatApiError(e, 'Could not load vouchers'));
        }
    }

    /**
     * The batch reference cell. The id is 64 hex characters — far too wide for a
     * table column, and nobody reads it in full — so show a short head and keep the
     * whole value on the title attribute for copying into a support ticket.
     */
    function batchReferenceCell(proofId: string | null): HTMLElement {
        if (!proofId) return createElement('span', { textContent: 'purchase' });
        const short = proofId.length > 12 ? `${proofId.slice(0, 12)}…` : proofId;
        const el = createElement('span', { textContent: short });
        el.setAttribute('title', proofId);
        return el;
    }

    function renderTable(rows: VoucherSummary[]): void {
        tableWrap.innerHTML = '';
        if (rows.length === 0) {
            tableWrap.appendChild(createElement('p', {
                className: 'cid-wizard-text', textContent: 'No vouchers match this filter.',
            }));
            return;
        }

        const table = createElement('table');
        const head = createElement('tr');
        for (const label of ['ID', 'Type', 'Status', 'Batch reference', 'Redeemed by', 'Expires', 'Note', '']) {
            head.appendChild(createElement('th', { textContent: label }));
        }
        table.appendChild(head);

        for (const v of rows) {
            const tr = createElement('tr');
            const cells: (string | HTMLElement)[] = [
                String(v.voucherId),
                TYPE_LABELS[v.registrationType] ?? v.registrationType,
                v.status,
                // Opaque batch reference for a purchase (registration-restructure
                // §14.4 — the ledger names no payment provider); the minting
                // delegate's CID for an operator mint. Shortened for the table: the
                // full 64-hex id is on the title attribute.
                v.provenance === 'payment'
                    ? batchReferenceCell(v.proofId)
                    : createElement('span', { textContent: v.mintedByCid ?? 'operator' }),
                v.redeemedByCid ?? '—',
                new Date(v.expiresAt).toLocaleDateString(),
                v.note ?? '—',
            ];
            for (const c of cells) {
                const td = createElement('td');
                if (typeof c === 'string') td.textContent = c;
                else td.appendChild(c);
                tr.appendChild(td);
            }

            const actionTd = createElement('td');
            if (canRevokeVoucher(v.status)) {
                const btn = createElement('button', {
                    className: 'cs-btn cs-btn--no cs-btn--small', textContent: 'Revoke',
                });
                btn.addEventListener('click', () => {
                    void (async () => {
                        if (!await ctx.shell.confirm(voucherRevokeConfirm(v.voucherId))) return;
                        btn.setAttribute('disabled', '');
                        try {
                            await revokeVoucher(v.voucherId);
                            await refresh();
                        } catch (e) {
                            showError(formatApiError(e, 'Could not revoke voucher'));
                            btn.removeAttribute('disabled');
                        }
                    })();
                });
                actionTd.appendChild(btn);
            }
            tr.appendChild(actionTd);
            table.appendChild(tr);
        }
        tableWrap.appendChild(table);
    }

    function renderPager(total: number): void {
        pager.innerHTML = '';
        pager.appendChild(createElement('span', {
            className: 'cid-wizard-text',
            textContent: `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`,
        }));
        if (offset > 0) {
            const prev = createElement('a', { className: 'cid-auth-link', textContent: 'Previous' });
            prev.href = '#';
            prev.addEventListener('click', (e) => {
                e.preventDefault();
                offset = Math.max(0, offset - PAGE_SIZE);
                void refresh();
            });
            pager.appendChild(prev);
        }
        if (offset + PAGE_SIZE < total) {
            const next = createElement('a', { className: 'cid-auth-link', textContent: 'Next' });
            next.href = '#';
            next.addEventListener('click', (e) => {
                e.preventDefault();
                offset += PAGE_SIZE;
                void refresh();
            });
            pager.appendChild(next);
        }
    }

    function showError(message: string): void {
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
    }

    await refresh();
}
